import React, { useState, useEffect, useCallback } from "react";
import { useDebounce } from "use-debounce";
import propTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  Input,
  Form,
  Alert,
  Row,
  Col,
  Modal,
  Checkbox,
  Space,
  Typography,
} from "antd";
import { AutoComplete } from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import useItemDetail from "../../hooks/useItemDetail";
import { URL_SCALE_LARGE } from "../../constance/urls";
import {
  defaultTruckCompanyDetail,
  defaultWoodSizeBuyPriceMapDetail,
  WOOD_IMPORT_GRADE_DISPLAY,
} from "../../constance/dataTypes";
import useSearchItem from "../../hooks/useSeatchItem";
import { Toast } from "../../utils/Toast";
import { BLUE_COLOR, PRIMARY_COLOR, WHITE_COLOR } from "../../constance/color";
import { decimalDigitFormatter } from "../../utils/stringFormat";
import ColorButton from "../common/ColorButton";

const WoodImportSettingsPriceModal = (props) => {
  const { open, onClose, onSuccess, detail, woodSizeBuyPriceMapDetail } = props;

  const [form] = Form.useForm();
  const { t } = useTranslation();
  const truckCompanies = useSearchItem({
    url: URL_SCALE_LARGE.TRUCK_COMPANY,
    description_render: "name",
    title_render: "code",
  });
  const truckCompanyDetail = useItemDetail({
    url: URL_SCALE_LARGE.TRUCK_COMPANY,
    id: detail ? detail.truck_company : null,
    manual: true,
    readOnly: true,
  });

  const truckCompanySearchTxt = Form.useWatch("truck_company_name", form);

  const [debouncedText] = useDebounce(truckCompanySearchTxt, 500);
  const [nameButton, setNameButton] = useState("");
  const [isColorButton, setIscolorButton] = useState({});
  const MIN_SEARCH_LENGTH = 2;
  const [hasSearched, setHasSearched] = React.useState(false);

  useEffect(() => {
    if (open) {
      setLocalErrorMessages(null);
      form.resetFields();
      form.setFieldsValue(detail || defaultWoodSizeBuyPriceMapDetail);
      setNameButton("confirm");
      setIscolorButton(PRIMARY_COLOR);
      setHasSearched(false);
      if (detail && detail.id) {
        truckCompanyDetail.fetchData();
        setNameButton("edit");
        setIscolorButton(BLUE_COLOR);
      }

      truckCompanyDetail.errorMessages = null;
      woodSizeBuyPriceMapDetail.errorMessages = null;
    }
  }, [open, detail]);

  React.useEffect(() => {
    if (!hasSearched) {
      setHasSearched(true);
      return;
    }

    if (debouncedText && debouncedText.length >= MIN_SEARCH_LENGTH) {
      truckCompanies.fetchData({ search: debouncedText });
    }
  }, [debouncedText]);

  const handleClose = () => {
    onClose();
    setHasSearched(false);
    truckCompanyDetail.errorMessages = null;
    woodSizeBuyPriceMapDetail.errorMessages = null;
  };

  const handleSubmit = async () => {
    try {
      setHasSearched(false);
      const values = await form.validateFields();
      const success = values.id
        ? await woodSizeBuyPriceMapDetail.handleSave(values)
        : await woodSizeBuyPriceMapDetail.handleCreate(values);
      if (success) {
        onClose();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        Toast.error(`${t("save")} ${t("error")}`);
      }
    } catch (error) {
      Toast.error(`${t("save")} ${t("error")}`);
    }
  };
  const [localErrorMessages, setLocalErrorMessages] = useState(null);

  useEffect(() => {
    if (
      truckCompanyDetail.errorMessages ||
      woodSizeBuyPriceMapDetail.errorMessages
    ) {
      setLocalErrorMessages({
        truck: truckCompanyDetail.errorMessages,
        wood: woodSizeBuyPriceMapDetail.errorMessages,
      });
    }
  }, [
    truckCompanyDetail.errorMessages,
    woodSizeBuyPriceMapDetail.errorMessages,
  ]);

  return (
    <Modal
      visible={open}
      centered
      title={
        detail?.id ? t("wood_imports.edit_code") : t("wood_imports.add_code")
      }
      width={800}
      onCancel={handleClose}
      footer={[
        <ColorButton
          key="cancel"
          onClick={handleClose}
          icon={<CloseOutlined />}
          color="danger"
          variant="solid"
        >
          {t("cancel")}
        </ColorButton>,
        <ColorButton
          type="primary"
          htmlType="submit"
          override={isColorButton}
          onClick={handleSubmit}
          icon={<CheckOutlined />}
          loading={
            truckCompanyDetail.isLoading || woodSizeBuyPriceMapDetail.isLoading
          }
        >
          {t(nameButton)}
        </ColorButton>,
      ]}
    >
      {localErrorMessages && (
        <Alert
          message={t("error")}
          description={
            <>
              <p>{localErrorMessages.truck}</p>
              <p>{localErrorMessages.wood}</p>
            </>
          }
          type="error"
          showIcon
        />
      )}{" "}
      <br />
      <Form form={form}>
        <Form.Item name="truck_company" hidden></Form.Item>
        <Form.Item name="id" hidden></Form.Item>
        <Form.Item
          label={t("truck_transactions.company")}
          name="truck_company_name"
          rules={[
            {
              required: true,
              message: t("error_messages.validation.required"),
            },
          ]}
        >
          <AutoComplete
            notFoundContent={truckCompanySearchTxt ? t("no_data") : ""}
            placeholder={`${t("search")} ${t("searchs.fields.code")} ${t(
              "or"
            )} ${t("searchs.fields.name")}...`}
            options={truckCompanies.response.map((company) => ({
              value: company.name,
              name: company.name,
              result: company,
              label: (
                <div>
                  <div>
                    <strong>{company.code}</strong>
                  </div>
                  <Typography.Text type="secondary">
                    {company.name}
                  </Typography.Text>
                </div>
              ),
            }))}
            onSelect={(value, option) => {
              form.setFieldValue("truck_company", option.result.code);
            }}
            loading={truckCompanies.isLoading}
          >
            <Input.Search
              loading={truckCompanies.isLoading}
              allowClear
            ></Input.Search>
          </AutoComplete>
        </Form.Item>
        <Form.Item name="vat" valuePropName="checked">
          <Checkbox>{t("wood_imports.vat")}</Checkbox>
        </Form.Item>
        <Form.Item
          label={t("wood_imports.wood_code")}
          name="wood_code"
          rules={[
            {
              required: true,
              message: t("error_messages.validation.required"),
            },
            {
              len: 10,
              message: `${t("error_messages.validation.length")} (10 ${t(
                "units.character"
              )})`,
            },
          ]}
        >
          <Input placeholder={`${t("wood_imports.wood_code")}...`} />
        </Form.Item>
      </Form>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Form layout="vertical" form={form}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <h3>{t("wood_imports.buy_price")}</h3>
              {WOOD_IMPORT_GRADE_DISPLAY.map((grade) => (
                <Form.Item
                  key={`buy_${grade}`}
                  label={t(`wood_imports.grades.${grade}`)}
                  name={`buy_${grade}`}
                >
                  <Input
                    addonAfter={t("units.baht")}
                    placeholder={t("wood_imports.buy_price")}
                    type="number"
                    onBlur={(e) => {
                      if (e.target.value === "" || e.target.value === null) {
                        form.setFieldsValue({ [`buy_${grade}`]: "0" });
                      } else
                        form.setFieldsValue({
                          [`buy_${grade}`]: decimalDigitFormatter(
                            e.target.value
                          ),
                        });
                    }}
                  />
                </Form.Item>
              ))}
            </Col>
            <Col span={12}>
              <h3>{t("wood_imports.sell_price")}</h3>
              {WOOD_IMPORT_GRADE_DISPLAY.map((grade) => (
                <Form.Item
                  key={`sell_${grade}`}
                  label={t(`wood_imports.grades.${grade}`)}
                  name={`sell_${grade}`}
                >
                  <Input
                    addonAfter={t("units.baht")}
                    placeholder={t("wood_imports.sell_price")}
                    type="number"
                    onBlur={(e) => {
                      if (e.target.value === "" || e.target.value === null) {
                        form.setFieldsValue({ [`sell_${grade}`]: "0" });
                      } else
                        form.setFieldsValue({
                          [`sell_${grade}`]: decimalDigitFormatter(
                            e.target.value
                          ),
                        });
                    }}
                  />
                </Form.Item>
              ))}
            </Col>
          </Row>
        </Form>
      </Space>
    </Modal>
  );
};

WoodImportSettingsPriceModal.defaultProps = {
  open: false,
  onClose: () => null,
  onSuccess: () => null,
  detail: defaultWoodSizeBuyPriceMapDetail,
  woodSizeBuyPriceMapDetail: {
    isLoading: false,
    errorMessages: null,
    handleSave: () => null,
    handleCreate: () => null,
  },
};

WoodImportSettingsPriceModal.propTypes = {
  open: propTypes.bool,
  onClose: propTypes.func,
  onSuccess: propTypes.func,
  detail: propTypes.object,
  woodSizeBuyPriceMapDetail: propTypes.object,
};

export default React.memo(WoodImportSettingsPriceModal);
